/**
 * Build a full app theme palette from a single CSS-parseable color (hex, name like "hot pink", etc.).
 */

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

/** Parse any CSS color the browser understands → #RRGGBB */
export const parseCssColorToHex = (input) => {
  if (typeof window === 'undefined' || input == null) return null;
  const raw = String(input).trim();
  if (!raw) return null;

  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw)) {
    let h = raw.slice(1);
    if (h.length === 3) {
      h = h.split('').map((c) => c + c).join('');
    }
    return `#${h.toUpperCase()}`;
  }

  const root = document.body || document.documentElement;
  if (!root) return null;
  const el = document.createElement('span');
  el.setAttribute('data-rebuildme-color-parse', '1');
  el.style.position = 'absolute';
  el.style.visibility = 'hidden';
  el.style.pointerEvents = 'none';
  el.style.color = raw;
  root.appendChild(el);
  const rgbStr = getComputedStyle(el).color;
  el.remove();
  const m = rgbStr.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!m) return null;
  const r = clamp(Number(m[1]), 0, 255);
  const g = clamp(Number(m[2]), 0, 255);
  const b = clamp(Number(m[3]), 0, 255);
  const toHex = (x) => clamp(Math.round(x), 0, 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
};

const hexToRgb = (hex) => {
  const h = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!h) return null;
  const n = parseInt(h[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const rgbToHex = ({ r, g, b }) => {
  const t = (x) => clamp(Math.round(x), 0, 255).toString(16).padStart(2, '0');
  return `#${t(r)}${t(g)}${t(b)}`.toUpperCase();
};

const rgbToHsl = ({ r, g, b }) => {
  const R = r / 255;
  const G = g / 255;
  const B = b / 255;
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const d = max - min;
  let h = 0;
  const l = (max + min) / 2;
  if (d < 1e-6) return { h: 0, s: 0, l: l * 100 };
  const s = d / (1 - Math.abs(2 * l - 1));
  switch (max) {
    case R:
      h = ((G - B) / d + (G < B ? 6 : 0)) / 6;
      break;
    case G:
      h = ((B - R) / d + 2) / 6;
      break;
    default:
      h = ((R - G) / d + 4) / 6;
      break;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
};

const hslToRgb = (h, s, l) => {
  const H = ((h % 360) + 360) % 360;
  const S = clamp(s, 0, 100) / 100;
  const L = clamp(l, 0, 100) / 100;
  const c = (1 - Math.abs(2 * L - 1)) * S;
  const x = c * (1 - Math.abs(((H / 60) % 2) - 1));
  const m = L - c / 2;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (H < 60) [rp, gp, bp] = [c, x, 0];
  else if (H < 120) [rp, gp, bp] = [x, c, 0];
  else if (H < 180) [rp, gp, bp] = [0, c, x];
  else if (H < 240) [rp, gp, bp] = [0, x, c];
  else if (H < 300) [rp, gp, bp] = [x, 0, c];
  else [rp, gp, bp] = [c, 0, x];
  return {
    r: (rp + m) * 255,
    g: (gp + m) * 255,
    b: (bp + m) * 255,
  };
};

const relLuminance = ({ r, g, b }) => {
  const lin = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const R = lin(r);
  const G = lin(g);
  const B = lin(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

/** Lettering on primary-colored bricks (header logo, Home acrostic): never reuse body text color. */
export const logoTextOnPrimaryHex = (primaryHex) => {
  const rgb = hexToRgb(primaryHex);
  if (!rgb) return '#F9F9FF';
  return relLuminance(rgb) > 0.52 ? '#1A1A22' : '#FAFAFF';
};

/**
 * @param {string} baseHex — #RRGGBB
 * @returns {object|null} palette fields matching preset COLOR_THEMES entries
 */
export const buildPaletteFromBaseHex = (baseHex) => {
  const rgb = hexToRgb(baseHex);
  if (!rgb) {
    return null;
  }
  const { h, s, l } = rgbToHsl(rgb);

  const isLightBase = l > 62;
  const primary = baseHex.toUpperCase();

  let secondary;
  let accent;
  let light;
  let textPrimary;
  let textSecondary;

  if (isLightBase) {
    secondary = rgbToHex(hslToRgb(h, clamp(s + 8, 15, 85), clamp(l - 22, 18, 55)));
    accent = rgbToHex(hslToRgb((h + 18) % 360, clamp(s + 12, 20, 90), clamp(l - 8, 35, 62)));
    light = rgbToHex(hslToRgb(h, Math.min(s * 0.35 + 8, 28), Math.min(l + 12, 96)));
    const lightRgb = hexToRgb(light);
    const onBg = relLuminance(lightRgb);
    textPrimary = onBg > 0.55 ? '#2D2D35' : '#F9F9FF';
    textSecondary = onBg > 0.55 ? '#5C5C66' : '#C8C8D8';
  } else {
    secondary = rgbToHex(hslToRgb(h, clamp(s * 0.85, 12, 70), clamp(l - 14, 8, 42)));
    accent = rgbToHex(hslToRgb((h + 22) % 360, clamp(s + 18, 25, 100), clamp(l + 18, 28, 72)));
    light = rgbToHex(hslToRgb(h, clamp(s * 0.45, 8, 45), clamp(l + 38, 88, 98)));
    const lightRgb = hexToRgb(light);
    const onBg = relLuminance(lightRgb);
    textPrimary = onBg > 0.55 ? '#1F1F28' : '#F4F4FF';
    textSecondary = onBg > 0.55 ? '#4A4A55' : '#B8B8CC';
  }

  return {
    name: 'AI Custom',
    primary,
    secondary,
    accent,
    light,
    textPrimary,
    textSecondary,
    logoText: logoTextOnPrimaryHex(primary),
    baseHex: primary,
    variant: 'light',
  };
};

/** Dark app shell: deep background, light body text; accent derived from base color. */
export const buildPaletteDarkShellFromBaseHex = (baseHex) => {
  const rgb = hexToRgb(baseHex);
  if (!rgb) return null;
  const { h, s } = rgbToHsl(rgb);
  const primary = baseHex.toUpperCase();
  const light = rgbToHex(hslToRgb(h, clamp(s * 0.35, 8, 42), 7));
  const secondary = rgbToHex(hslToRgb(h, clamp(s * 0.5, 10, 55), 13));
  const accent = rgbToHex(hslToRgb((h + 28) % 360, clamp(s + 12, 28, 90), 54));
  return {
    name: 'AI Custom',
    primary,
    secondary,
    accent,
    light,
    textPrimary: '#ECEBF2',
    textSecondary: '#A5A3B8',
    logoText: logoTextOnPrimaryHex(primary),
    baseHex: primary,
    variant: 'dark',
  };
};

/**
 * @param {string} colorInput — hex or CSS color phrase
 * @param {string} [labelHint] — short label from user or AI
 * @param {'light'|'dark'} [variant]
 */
export const buildCustomThemeFromColorInput = (colorInput, labelHint = '', variant = 'light') => {
  const hex = parseCssColorToHex(colorInput);
  if (!hex) return null;
  const palette =
    variant === 'dark' ? buildPaletteDarkShellFromBaseHex(hex) : buildPaletteFromBaseHex(hex);
  if (!palette) return null;
  const label = String(labelHint || colorInput || '').trim().slice(0, 48) || 'Custom';
  return { ...palette, baseHex: hex, label };
};
